import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import client from '@/lib/api';
import { Loader2, CheckCircle2 } from 'lucide-react';

export default function ContactPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', company_email: '', company: '', industry: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company_email || !form.message) {
      toast({ title: 'Missing fields', description: 'Name, company email and message are required.' });
      return;
    }
    setLoading(true);
    try {
      await client.post('/contact', form);
      setSent(true);
      toast({ title: 'Request received', description: 'Our team will reach out within one business day.' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 viso-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">Talk to us</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight"><span className="text-gradient">Book a live demo of Visotonics.</span></h1>
          <p className="text-slate-400 mt-4 max-w-md">Share your use case — seaport, warehouse, airport or enterprise — and we’ll set up a working session with our solutions team.</p>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            <div>· Live pilot in under 30 days</div>
            <div>· On-prem, cloud or air-gapped</div>
            <div>· Value case with named executive sponsor</div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 space-y-4">
          {sent ? (
            <div className="flex flex-col items-center text-center py-10">
              <CheckCircle2 className="w-10 h-10 text-cyan-300" />
              <div className="font-display text-2xl mt-4">Request received</div>
              <p className="text-slate-400 mt-2">A member of the Visotonics team will contact you shortly.</p>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-slate-300">Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-slate-300">Company email *</Label>
                <Input type="email" value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="jane@company.com" />
              </div>
              <div>
                <Label className="text-slate-300">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="Acme Terminals" />
              </div>
              <div>
                <Label className="text-slate-300">Industry</Label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Seaport">Seaport</SelectItem>
                    <SelectItem value="Warehouse">Warehouse</SelectItem>
                    <SelectItem value="Airport">Airport</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Message *</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="bg-black/30 border-white/10 mt-1" placeholder="Tell us about your operations..." />
              </div>
              <Button disabled={loading} className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send request
              </Button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
